import React, { useState } from "react";
import cx from "classnames";
import { useToggle } from "usehooks-ts";
import { useHistory } from "react-router-dom";
import { useTranslation } from "i18n";
import { ImageLocation } from "models";
import SectionControl from "pages/Profile/components/SectionControl";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import ThumbnailButton from "pages/Profile/components/GallerySection/ThumbnailButton";
import ImageNavButton from "pages/Profile/components/GallerySection/ImageNavButton";
import GalleryMediaViewer from "pages/Profile/components/GallerySection/GalleryMediaViewer";
import SlideButton from "pages/Profile/components/common/SlideButton";
import { useGetGallery } from "pages/Profile/hooks/useGetGallery";
import { EDIT_PHOTO_FULL_PATH } from "shared/constants";
import { getImageUrl } from "services/UrlBuilder";
import { dataTestIds } from "data-testids";
import getImageAltText from "pages/Profile/utils/getImageAltText";

export interface GalleryProps {
  isHeadExpanded?: boolean;
  toggleHeadExpanded: () => void;
  isEdit?: boolean;
}

const Gallery = ({
  isHeadExpanded,
  toggleHeadExpanded,
  isEdit,
}: GalleryProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { gallery, isLoading } = useGetGallery();
  const [fullScreenViewerOpen, toggleFullScreenViewer] = useToggle();
  const [selectedIndex, setCurrentIndex] = useState(0);

  const nextSlide = () =>
    setCurrentIndex((prev) => (prev + 1) % gallery.length);
  const prevSlide = () =>
    setCurrentIndex((prev) => (prev - 1 + gallery.length) % gallery.length);

  const isNavVisible = gallery.length > 1;

  return (
    <div
      className="relative grid gap-6"
      data-testid={dataTestIds.profile["Profile.ProfileCard.gallerySection"]}
    >
      <h2 className="hidden">{t("performer:profile.section.photos")}</h2>
      <SectionControl
        isExpanded={isHeadExpanded}
        toggleClick={toggleHeadExpanded}
        fullScreenClick={toggleFullScreenViewer}
      />
      <ProfileCard
        className="flex aspect-[3/4] min-w-0 items-center justify-center p-0"
        iconClassName="text-white drop-shadow-profile"
        isEdit={isEdit}
        editBottom
        onClickEdit={() => history.push(EDIT_PHOTO_FULL_PATH)}
        isLoading={isLoading}
      >
        <SlideButton
          className={cx(
            "slide-button--drop-shadow absolute left-3 top-1/2 z-[1] text-white",
            {
              hidden: !isNavVisible,
            }
          )}
          direction="prev"
          onClick={prevSlide}
        />
        <SlideButton
          className={cx(
            "slide-button--drop-shadow absolute right-3 top-1/2 z-[1] text-white",
            {
              hidden: !isNavVisible,
            }
          )}
          direction="next"
          onClick={nextSlide}
        />
        <button
          className="flex h-full w-full items-center justify-center"
          onClick={toggleFullScreenViewer}
          data-testid={dataTestIds.profile["Profile.GallerySection.Headshot"]}
          aria-label={t("common:aria.label.openGalleryImageFullscreen", {
            index: selectedIndex + 1,
            total: gallery.length,
          })}
        >
          {gallery.map((photo, index) => {
            return (
              <img
                key={`photo-${index}`}
                src={getImageUrl(photo.url, ImageLocation.ViewPhoto)}
                alt={getImageAltText({
                  t,
                  description: photo?.description,
                  photographer: photo?.photographer,
                })}
                className={cx(
                  "block max-h-full max-w-full rounded-[16px] object-cover",
                  index === 0 ? "h-full w-full" : "h-fit w-fit",
                  {
                    hidden: index !== selectedIndex,
                  }
                )}
                data-testid={photo?.userFriendlyId.split("-")[1]}
              />
            );
          })}
        </button>
        {!isHeadExpanded && isNavVisible && (
          <div className="absolute bottom-12 left-0 z-[1] flex w-full justify-center gap-1">
            {gallery.map((_, index) => {
              return (
                <ImageNavButton
                  key={`photo-nav-button-${index}`}
                  onClick={() => setCurrentIndex(index)}
                  isSelected={selectedIndex === index}
                  aria-label={t("common:aria.label.goToGalleryImage", {
                    index: index + 1,
                    total: gallery.length,
                  })}
                />
              );
            })}
          </div>
        )}
        <div className="absolute bottom-2 left-3 text-s text-white drop-shadow-profile">
          {gallery[selectedIndex]?.photographer &&
            `© ${gallery[selectedIndex].photographer}`}
        </div>
      </ProfileCard>
      {isHeadExpanded && (
        <div
          className="flex flex-nowrap gap-2 overflow-x-auto p-1"
          data-testid={
            dataTestIds.profile[
              "Profile.GallerySection.ExpandedView.GalleryCollection"
            ]
          }
        >
          {gallery.map((photo, index) => {
            return (
              <ThumbnailButton
                key={`photo-button-${index}`}
                isSelected={index === selectedIndex}
                onClick={() => setCurrentIndex(index)}
                aria-label={t("common:aria.label.goToGalleryImage", {
                  index: index + 1,
                  total: gallery.length,
                })}
              >
                <img
                  src={getImageUrl(photo.url, ImageLocation.ViewPhoto)}
                  alt={getImageAltText({
                    t,
                    description: photo?.description,
                    photographer: photo?.photographer,
                  })}
                  className={cx(
                    "block h-full w-full rounded-[10px] object-cover"
                  )}
                  data-testid={photo?.userFriendlyId.split("-")[1]}
                />
              </ThumbnailButton>
            );
          })}
        </div>
      )}
      {fullScreenViewerOpen && (
        <GalleryMediaViewer
          toggleFullScreenViewer={toggleFullScreenViewer}
          selectedIndex={selectedIndex}
          gallery={gallery}
        />
      )}
    </div>
  );
};

export default Gallery;
